// frontend/src/components/TransferenciaMilhasModal/TransferenciaMilhasModal.jsx

import React, { useState, useEffect } from 'react';
import { Modal, Form, Select, InputNumber, DatePicker, Button, message, Row, Col, Switch, Input, Divider, Typography, Space } from 'antd';
import { getPortadores, getProgramasDoPortador } from '../../services/milhasService';
import { createTransferenciaMilhas } from '../../services/milhasService';
import dayjs from 'dayjs';
// 1. IMPORTAÇÃO DO TRATAMENTO DE ERRO
import { getErrorMessage } from '../../utils/errorHandler';
import { decimalInputProps } from '../../utils/numberInputMask';
import MobileLaunchSheet from '../MobileLaunchSheet/MobileLaunchSheet';

const { Option } = Select;
const { Text } = Typography;

// Investimento do programa: usa o valor gravado; sem ele, deriva de saldo × CPM.
const investimentoDoPrograma = (programa) => {
    const informado = programa.valor_total_investido;
    if (informado !== null && informado !== undefined && informado !== '') {
        return parseFloat(informado) || 0;
    }
    return (parseFloat(programa.saldo_atual) || 0) * (parseFloat(programa.custo_medio_milheiro) || 0) / 1000;
};

const paridadeInputProps = decimalInputProps(2, {
    style: { width: '40%' },
    min: 0.01,
});

const TransferenciaMilhasModal = ({ visible, onClose, onSuccess, isMobile = false }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [portadores, setPortadores] = useState([]);
    const [programas, setProgramas] = useState([]);
    const [selectedProgramaOrigem, setSelectedProgramaOrigem] = useState(null);
    const [totalAReceber, setTotalAReceber] = useState(0);
    const [cpmDaOperacao, setCpmDaOperacao] = useState(0);
    const [cpmDestinoApos, setCpmDestinoApos] = useState(null);

    // Hook de mensagem para consistência
    const [messageApi, contextHolder] = message.useMessage();

    const formValues = Form.useWatch([], form);
    const compraHabilitada = Form.useWatch('compra_habilitada', form);
    const isBumerangue = Form.useWatch('is_bumerangue', form);
    const programaDestinoId = Form.useWatch('programa_destino', form);


    useEffect(() => {
        if (visible) {
            getPortadores()
                .then(response => {
                    const lista = response.data || [];
                    setPortadores(lista);
                    // Com um único portador não há o que escolher: já deixa preenchido.
                    if (lista.length === 1) {
                        form.setFieldsValue({ portador: lista[0].id });
                        handlePortadorChange(lista[0].id);
                    }
                })
                .catch(error => {
                    // 2. USO NO FETCH PORTADORES
                    messageApi.error(getErrorMessage(error, 'Erro ao carregar portadores.'));
                });
        } else {
            form.resetFields();
            setSelectedProgramaOrigem(null);
            setTotalAReceber(0);
            setCpmDaOperacao(0);
            setCpmDestinoApos(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, form, messageApi]);

    useEffect(() => {
        if (!formValues || !selectedProgramaOrigem) {
            setTotalAReceber(0);
            setCpmDaOperacao(0);
            setCpmDestinoApos(null);
            return;
        }

        const {
            quantidade_milhas_saldo = 0,
            quantidade_milhas_compradas = 0,
            custo_milhas_compradas = 0,
            paridade_envio = 1,
            paridade_recebimento = 1,
            percentual_bonus = 0,
            percentual_bonus_bumerangue = 0,
        } = formValues;

        const totalMilhasEnviadas = (quantidade_milhas_saldo || 0) + (compraHabilitada ? (quantidade_milhas_compradas || 0) : 0);
        if (totalMilhasEnviadas === 0) {
            setTotalAReceber(0);
            setCpmDaOperacao(0);
            setCpmDestinoApos(null);
            return;
        }

        const milhasConvertidas = (totalMilhasEnviadas / (paridade_envio || 1)) * (paridade_recebimento || 1);
        const milhasBonus = milhasConvertidas * ((percentual_bonus || 0) / 100);
        const totalRecebido = milhasConvertidas + milhasBonus;

        const compradas = compraHabilitada ? (quantidade_milhas_compradas || 0) : 0;
        const custoCompra = compraHabilitada ? (custo_milhas_compradas || 0) : 0;
        const saldoAtual = parseFloat(selectedProgramaOrigem.saldo_atual) || 0;
        const investimentoAtual = investimentoDoPrograma(selectedProgramaOrigem);
        const saldoAposCarrinho = saldoAtual + compradas;
        const investimentoAposCarrinho = investimentoAtual + custoCompra;
        const cpmAposCarrinho = saldoAposCarrinho > 0
            ? (investimentoAposCarrinho / saldoAposCarrinho) * 1000
            : 0;
        const custoDebito = (totalMilhasEnviadas / 1000) * cpmAposCarrinho;
        const milhasBumerangue = isBumerangue
            ? totalMilhasEnviadas * ((percentual_bonus_bumerangue || 0) / 100)
            : 0;
        const custoOperacao = Math.max(0, custoDebito - (milhasBumerangue / 1000) * cpmAposCarrinho);
        const cpm = totalRecebido > 0 ? (custoOperacao / totalRecebido) * 1000 : 0;

        // Destino após creditar principal + bônus: o crédito leva todo o custo da operação
        // e o bônus entra a custo zero (mesma regra do backend ao confirmar as pernas).
        const destino = programas.find(p => p.id === programaDestinoId);
        let cpmDestino = null;
        if (destino && destino.id !== selectedProgramaOrigem.id) {
            const saldoDestinoApos = (parseFloat(destino.saldo_atual) || 0) + totalRecebido;
            const investimentoDestinoApos = investimentoDoPrograma(destino) + custoOperacao;
            cpmDestino = saldoDestinoApos > 0 ? (investimentoDestinoApos / saldoDestinoApos) * 1000 : 0;
        }

        setTotalAReceber(totalRecebido);
        setCpmDaOperacao(cpm);
        setCpmDestinoApos(cpmDestino);
    }, [formValues, selectedProgramaOrigem, compraHabilitada, isBumerangue, programas, programaDestinoId]);

    const handlePortadorChange = (portadorId) => {
        form.resetFields(['programa_origem', 'programa_destino']);
        setProgramas([]);
        setSelectedProgramaOrigem(null);
        if (portadorId) {
            getProgramasDoPortador(portadorId)
                .then(response => setProgramas(response.data))
                .catch(error => {
                    // 3. USO NO FETCH PROGRAMAS
                    messageApi.error(getErrorMessage(error, 'Erro ao carregar programas do portador.'));
                });
        }
    };
    
    const handleProgramaOrigemChange = (programaId) => {
        const programa = programas.find(p => p.id === programaId);
        setSelectedProgramaOrigem(programa || null);
    };

    const handleFormSubmit = async (closeOnSuccess = true) => {
        try {
            const values = await form.validateFields();
            setLoading(true);
            
            const payload = {
                ...values,
                quantidade_milhas_compradas: values.compra_habilitada ? values.quantidade_milhas_compradas : 0,
                custo_milhas_compradas: values.compra_habilitada ? values.custo_milhas_compradas : 0,
                percentual_bonus_bumerangue: values.is_bumerangue ? values.percentual_bonus_bumerangue : 0,
                data_solicitacao: values.data_solicitacao.format('YYYY-MM-DD'),
                data_credito_principal: values.data_credito_principal ? values.data_credito_principal.format('YYYY-MM-DD') : null,
                data_credito_bonus: values.data_credito_bonus ? values.data_credito_bonus.format('YYYY-MM-DD') : null,
                data_credito_bumerangue: values.is_bumerangue ? (values.data_credito_bumerangue ? values.data_credito_bumerangue.format('YYYY-MM-DD') : null) : null,
            };

            await createTransferenciaMilhas(payload);
            
            onSuccess(closeOnSuccess, 'Transferência registrada! Confirme na lista de movimentações para efetivá-la.');

            if (!closeOnSuccess) {
                const currentPortador = values.portador;
                form.resetFields();
                form.setFieldsValue({ 
                    portador: currentPortador,
                    paridade_envio: 1,
                    paridade_recebimento: 1,
                    percentual_bonus: 0,
                });
                // Manter programas carregados
                if(currentPortador) {
                      // Programas já estão em state, não precisa recarregar
                }
                setSelectedProgramaOrigem(null);
                setTotalAReceber(0);
                setCpmDaOperacao(0);
                setCpmDestinoApos(null);
            }

        } catch (error) {
            // 4. USO NO SUBMIT
            messageApi.error(getErrorMessage(error, 'Erro ao registrar transferência. Verifique os campos.'));
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = () => handleFormSubmit(true);
    const handleSubmitAndAddNew = () => handleFormSubmit(false);

    const handleCancel = () => {
        onClose();
    };

    const formatarReais = (valor) => valor.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const saldoLabel = selectedProgramaOrigem ? `Saldo Atual (${selectedProgramaOrigem.programa_nome})` : 'Saldo Atual';
    const programaDestinoSelecionado = programas.find(p => p.id === programaDestinoId);
    const totalReceberLabel = programaDestinoSelecionado ? `Total a Receber (${programaDestinoSelecionado.programa_nome})` : 'Total a Receber';
    // Rótulo curto e fixo para caber em uma linha e alinhar com os vizinhos; o detalhe vai no tooltip.
    const cpmDestinoTooltip = programaDestinoSelecionado
        ? `CPM de ${programaDestinoSelecionado.programa_nome} depois de creditar o principal e o bônus. Hoje: R$ ${formatarReais(parseFloat(programaDestinoSelecionado.custo_medio_milheiro) || 0)}.`
        : 'CPM do programa de destino depois de creditar o principal e o bônus.';

    const formInner = (
            <Form form={form} layout="vertical" size={isMobile ? 'large' : 'middle'} initialValues={{ 
                paridade_envio: 1,
                paridade_recebimento: 1,
                percentual_bonus: 0,
               }}>
                
                <Row gutter={16}>
                    <Col span={12}><Form.Item name="portador" label="Portador" rules={[{ required: true }]}><Select placeholder="Selecione" onChange={handlePortadorChange}>{portadores.map(p => <Option key={p.id} value={p.id}>{p.nome_completo}</Option>)}</Select></Form.Item></Col>
                    <Col span={12}><Form.Item name="data_solicitacao" label="Data da Solicitação" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                </Row>

                <Row gutter={16}>
                    <Col span={12}><Form.Item name="programa_origem" label="Programa de Origem" rules={[{ required: true }]}><Select placeholder="Selecione" disabled={!programas.length} onChange={handleProgramaOrigemChange}>{programas.map(p => <Option key={p.id} value={p.id}>{p.programa_nome}</Option>)}</Select></Form.Item></Col>
                    <Col span={12}><Form.Item name="programa_destino" label="Programa de Destino" rules={[{ required: true }]}><Select placeholder="Selecione" disabled={!programas.length}>{programas.map(p => <Option key={p.id} value={p.id}>{p.programa_nome}</Option>)}</Select></Form.Item></Col>
                </Row>
                
                <Row gutter={16}>
                    <Col span={8}>
                        <Form.Item label={saldoLabel}>
                            <InputNumber style={{ width: '100%', color: 'rgba(0, 0, 0, 0.88)' }} value={selectedProgramaOrigem?.saldo_atual || 0} disabled formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(value) => value.replace(/\$\s?|(,*)/g, '')} />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item label="Custo Médio (CPM)">
                            <InputNumber style={{ width: '100%', color: 'rgba(0, 0, 0, 0.88)' }} value={selectedProgramaOrigem?.custo_medio_milheiro || 0} precision={2} disabled prefix="R$ " />
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="quantidade_milhas_saldo" label="Milhas a Enviar" rules={[{ required: true }]}>
                            <InputNumber style={{ width: '100%' }} min={0} />
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={16}>
                    <Col span={6}>
                        <Form.Item label="Paridade">
                            <Input.Group compact>
                                <Form.Item name="paridade_envio" noStyle rules={[{ required: true }]}>
                                    <InputNumber {...paridadeInputProps} />
                                </Form.Item>
                                <Input style={{ width: '20%', textAlign: 'center' }} placeholder=":" disabled />
                                <Form.Item name="paridade_recebimento" noStyle rules={[{ required: true }]}>
                                    <InputNumber {...paridadeInputProps} />
                                </Form.Item>
                            </Input.Group>
                        </Form.Item>
                    </Col>
                    <Col span={6}><Form.Item name="data_credito_principal" label="Dia Crédito Principal" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                    <Col span={6}><Form.Item name="percentual_bonus" label="Bônus (%)"><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                    <Col span={6}><Form.Item name="data_credito_bonus" label="Dia Crédito Bônus" rules={[{ required: form.getFieldValue('percentual_bonus') > 0 }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                </Row>

                <Form.Item name="observacao" label="Observação">
                    <Input.TextArea rows={2} placeholder="Adicione uma observação para esta transferência" />
                </Form.Item>

                <Divider style={{ marginTop: 0, marginBottom: 16 }}/>
                
                <Row gutter={16} align="middle">
                    <Col>
                        <Space>
                            <Form.Item name="compra_habilitada" valuePropName="checked" noStyle><Switch /></Form.Item>
                            <Text>Comprar milhas no carrinho?</Text>
                        </Space>
                    </Col>
                     <Col>
                        <Space>
                            <Form.Item name="is_bumerangue" valuePropName="checked" noStyle><Switch /></Form.Item>
                            <Text>É Bumerangue?</Text>
                        </Space>
                    </Col>
                </Row>

                 {compraHabilitada && (
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={12}><Form.Item name="quantidade_milhas_compradas" label="Milhas Compradas" rules={[{ required: compraHabilitada }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                        <Col span={12}>
                            <Form.Item name="custo_milhas_compradas" label="Custo da Compra (R$)" rules={[{ required: compraHabilitada }]}>
                                <InputNumber
                                    prefix="R$ "
                                    style={{ width: '100%' }}
                                    precision={2}
                                    step={0.01}
                                    formatter={(value) => {
                                        if (value === null || value === undefined || value === '') return '';
                                        return new Intl.NumberFormat('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                        }).format(value);
                                    }}
                                    parser={(value) => {
                                        if (!value) return null;
                                        const onlyNumbers = value.replace(/[^\d]/g, '');
                                        if (!onlyNumbers) return null;
                                        const num = parseInt(onlyNumbers, 10);
                                        return num / 100;
                                    }}
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                )}
                {isBumerangue && (
                    <Row gutter={16} style={{ marginTop: 16 }}>
                        <Col span={12}><Form.Item name="percentual_bonus_bumerangue" label="Bônus Bumerangue (%)" rules={[{ required: isBumerangue }]}><InputNumber style={{ width: '100%' }} min={0} /></Form.Item></Col>
                        <Col span={12}><Form.Item name="data_credito_bumerangue" label="Dia Crédito Bumerangue" rules={[{ required: form.getFieldValue('percentual_bonus_bumerangue') > 0 }]}><DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" /></Form.Item></Col>
                    </Row>
                )}
                
                <Divider orientation="left" plain>Resultado da Operação</Divider>
                <Row gutter={16} align="bottom">
                    <Col span={isMobile ? 24 : 8}>
                        <Form.Item label={totalReceberLabel}>
                            <InputNumber style={{ width: '100%', color: 'rgba(0, 0, 0, 0.88)' }} value={totalAReceber} precision={0} disabled formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')} parser={(value) => value.replace(/\$\s?|(,*)/g, '')} />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 8}>
                        <Form.Item label="Custo Médio da Operação (CPM)">
                            <InputNumber style={{ width: '100%', color: 'rgba(0, 0, 0, 0.88)' }} value={cpmDaOperacao} precision={2} disabled prefix="R$ " />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 8}>
                        <Form.Item
                            label="Novo CPM do Destino"
                            tooltip={cpmDestinoTooltip}
                        >
                            <InputNumber style={{ width: '100%', color: 'rgba(0, 0, 0, 0.88)' }} value={cpmDestinoApos} placeholder="Escolha o destino" precision={2} disabled prefix="R$ " />
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
    );

    return (
        <>
            {contextHolder}
            {isMobile ? (
                <MobileLaunchSheet
                    open={visible}
                    title="Transferência de Milhas"
                    onCancel={handleCancel}
                    onSave={handleSubmit}
                    saveLoading={loading}
                    secondaryAction={
                        <Button type="default" loading={loading} onClick={handleSubmitAndAddNew} block size="large">
                            Salvar e Adicionar Outra
                        </Button>
                    }
                >
                    {formInner}
                </MobileLaunchSheet>
            ) : (
                <Modal
                    title="Registrar Nova Transferência de Milhas"
                    open={visible}
                    onCancel={handleCancel}
                    width={800}
                    destroyOnClose
                    footer={[
                        <Button key="back" onClick={handleCancel}>Cancelar</Button>,
                        <Button key="add" type="default" loading={loading} onClick={handleSubmitAndAddNew}>Salvar e Adicionar Outra</Button>,
                        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>Salvar</Button>,
                    ]}
                >
                    {formInner}
                </Modal>
            )}
        </>
    );
};

export default TransferenciaMilhasModal;